import React from 'react';
import ProgressBar, {
  ProgressBarProps as BootstrapProgressBarProps,
} from 'react-bootstrap/ProgressBar';
import { MessageDescriptor, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Icon, IconType } from '../Icon';
import { FlexRowCenterVertical, StyleUtils } from '../StyledWrappers';
import { IWithClassName } from '../types';
import { StyledValue } from './wrappers';

export interface ProgressProps
  extends Partial<Pick<BootstrapProgressBarProps, 'variant'>>,
    IWithClassName {
  /** the current value (defaults to 0) */
  value?: number;
  /** the min value (defaults to 0) */
  min?: number;
  /** the max value (defaults to 100) */
  max?: number;
  /** should we show a fraction (2/5) instead of a percent (40%)? */
  showFraction?: boolean;
  /** hide success/failure icon */
  hideIcon?: boolean;
  /** hide the label under the progress bar */
  hideLabel?: boolean;
  /** unit of measure used in fraction representation */
  unit?: MessageDescriptor;
  /** invert progress visualization */
  invertVisualization?: boolean;
  /** height of the progress bar */
  barHeight?: string;
  /** fill color of the progress bar */
  barBackgroundColor?: string;
  /** border radius of the progress bar */
  barBorderRadius?: string;
}

const StyledProgressBar = styled(ProgressBar)<{
  /** height of the progress bar */
  barHeight?: string;
  /** fill color of the progress bar */
  barBackgroundColor?: string;
  /** border color of the progress bar */
  barBorderColor?: string;
  /** border width of the progress bar */
  barBorderWidth?: string;
  /** border radius of the progress bar */
  barBorderRadius?: string;
}>`
  ${({ barHeight }) => barHeight && `height: ${barHeight};`}
  .progress-bar {
    ${({ barBackgroundColor }) =>
      barBackgroundColor && `background-color: ${barBackgroundColor};`}
    ${({ barBorderRadius }) =>
      barBorderRadius && `border-radius: ${barBorderRadius};`}
  }
`;

/**
 * Progress variant
 */
export enum ProgressVariant {
  Success = 'success',
  Danger = 'danger',
  Warning = 'warning',
  Info = 'info',
  Default = 'default',
}

const VARIANT_ICONS: Record<ProgressVariant, IconType | undefined> = {
  success: 'checkmark',
  danger: 'close',
  warning: undefined,
  info: undefined,
  default: undefined,
};

/**
 * Progress
 */
export const Progress: React.FC<ProgressProps> = ({
  className,
  value,
  max,
  min,
  showFraction,
  variant,
  hideIcon,
  hideLabel = false,
  unit,
  barHeight,
  barBackgroundColor,
  barBorderRadius,
  invertVisualization = false,
}) => {
  const { formatNumber, formatMessage } = useIntl();
  const maxOrDefault = max || 100;
  const valueOrDefault = value ?? 0;
  const minOrDefault = min ?? 0;
  const isComplete = valueOrDefault === maxOrDefault;

  const icon = VARIANT_ICONS[variant ?? ProgressVariant.Default];

  return (
    <>
      <StyledProgressBar
        className={className}
        min={min}
        max={max}
        now={invertVisualization ? maxOrDefault - valueOrDefault : value}
        variant={
          variant === ProgressVariant.Default
            ? undefined
            : variant ?? (isComplete ? 'success' : undefined)
        }
        barHeight={barHeight}
        barBackgroundColor={barBackgroundColor}
        barBorderRadius={barBorderRadius}
      />
      {!hideLabel && (
        <FlexRowCenterVertical
          style={{ color: 'inherit', gap: StyleUtils.Spacing.sm }}
        >
          {showFraction ? (
            <StyledValue style={{ fontSize: 12 }}>
              {`${valueOrDefault}/${maxOrDefault}${
                unit ? ` ${formatMessage(unit)}` : ''
              }`}
            </StyledValue>
          ) : (
            <StyledValue>
              {formatNumber(
                Math.round(
                  ((valueOrDefault - minOrDefault) /
                    (maxOrDefault - minOrDefault)) *
                    100,
                ) / 100,
                { style: 'percent' },
              )}
            </StyledValue>
          )}
          {!hideIcon && icon && (
            <Icon type={icon} {...(icon === 'close' ? { size: 10 } : {})} />
          )}
        </FlexRowCenterVertical>
      )}
    </>
  );
};
